import {t} from "@lingui/core/macro";
import {StyleSheet} from "aphrodite";
import * as React from "react";
import {useId} from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {OptionItem, SingleSelect} from "@khanacademy/wonder-blocks-dropdown";
import {LabeledField} from "@khanacademy/wonder-blocks-labeled-field";
import {semanticColor, sizing} from "@khanacademy/wonder-blocks-tokens";
import {BodyText} from "@khanacademy/wonder-blocks-typography";
import {getLanguages} from "@khan/i18n";
import {AppShellContext} from "@khan/shared-core/context";

import useLocalesQuery from "./use-locales-query.ts";

interface Props {
    // TODO(michaelpolyak): Rename this to `userKaLocale` as that's what it is.
    userLanguage: string;
    onChange: (userKaLocale: string | "unspecified") => void;
    disabled: boolean;
    label?: React.ReactNode;
}

const MAX_HEIGHT = 320;

const LanguageSelector = ({
    userLanguage: userKaLocale,
    disabled,
    onChange,
    label = t`Primary Language`,
}: Props): React.ReactNode => {
    const {entrypoint} = React.useContext(AppShellContext);
    const languageSelectorId = useId();
    const {locales, isLoading, isError} = useLocalesQuery();

    const isKhanClassroom = entrypoint === "classroom";

    const kaLocales = getLanguages();

    const selectableLocales: Array<
        React.ReactElement<React.ComponentProps<typeof OptionItem>>
    > = [
        <OptionItem
            key="unspecified"
            label={t`Not specified`}
            // NOTE: This "unspecified" is a sentinal value
            // keyed on by the backend settings mutation,
            // to clear the user preferred KA locale.
            // sync-start:user-ka-locale-unspecified 2106202621 https://github.com/Khan/webapp/blob/master/services/users/mutations/set_settings.go
            value="unspecified"
            // sync-end:user-ka-locale-unspecified
        />,
    ];

    let unsupportedUserLocale = false;
    let localesNotFullySupportingKhanmigo = false;

    for (const locale of locales) {
        let option: React.ReactElement<
            React.ComponentProps<typeof OptionItem>
        > | null = null;

        let languageName = kaLocales.get(locale.id)?.languageName;

        if (!languageName || locale.localName === languageName) {
            languageName = locale.localName;
        } else {
            languageName = `${locale.localName} - ${languageName}`;
        }

        if (isKhanClassroom) {
            if (locale.khanmigoSupport !== "FULL") {
                languageName += " *";
                // We need to highlight that some of the selectable KA locales
                // are not fully supported by Khanmigo, since the chat language
                // is driven from this selection in Khan Classroom.
                localesNotFullySupportingKhanmigo = true;
            }

            // When the language selector is shown in context of Khan Classroom,
            // only the supported KA locales may be selected as user preferred.
            if (locale.isSupportedInKhanClassroom) {
                option = (
                    <OptionItem
                        key={locale.id}
                        label={languageName}
                        value={locale.id}
                    />
                );
            } else {
                // If the user selected KA locale is not supported in Classroom,
                // it will still appear in the list to indicate it's the current
                // selection, but it will appear disabled.
                if (locale.id === userKaLocale) {
                    option = (
                        <OptionItem
                            key={locale.id}
                            label={languageName}
                            value={locale.id}
                            disabled={true}
                        />
                    );
                    // In addition, we prompt the user in the field messaging
                    // to change their selection.
                    unsupportedUserLocale = true;
                }
            }
        } else {
            // In all the other apps there is no restriction on the locales
            // shown in the language selector.
            option = (
                <OptionItem
                    key={locale.id}
                    label={languageName}
                    value={locale.id}
                />
            );
        }

        if (option) {
            selectableLocales.push(option);
        }
    }

    return (
        <View style={{gap: sizing.size_120}}>
            <LabeledField
                label={label}
                description={
                    unsupportedUserLocale &&
                    t`Your currently selected language is not supported. Please select a new language.`
                }
                field={
                    <SingleSelect
                        disabled={isLoading || isError || disabled}
                        onChange={onChange}
                        placeholder={t`Language`}
                        selectedValue={userKaLocale}
                        style={styles.dropdown}
                        dropdownStyle={styles.dropdownStyle}
                        id={languageSelectorId}
                    >
                        {selectableLocales}
                    </SingleSelect>
                }
            />
            {localesNotFullySupportingKhanmigo && (
                <BodyText size="small" style={styles.khanmigoNotFullySupported}>
                    {t`Languages marked with an * have not been fully quality-tested with Khanmigo’s responses and you may notice occasional errors.`}
                </BodyText>
            )}
        </View>
    );
};
export default LanguageSelector;

const styles = StyleSheet.create({
    dropdown: {
        width: "100%",
    },
    dropdownStyle: {
        maxHeight: MAX_HEIGHT,
    },
    khanmigoNotFullySupported: {
        color: semanticColor.text.secondary,
    },
});
